import { useEffect, useState } from 'react';
import type { UserRole, UserPlan } from '@/types/user';

export type Playstyle = 'scorer' | 'shooter' | 'slasher' | 'playmaker' | 'defender' | 'all-around';

export interface PlayerProfileFields {
  position?: string;
  height?: string;
  age?: string;
  level?: string;
  availability?: string;
  playstyle?: Playstyle;
}

export interface ClubProfileFields {
  clubName?: string;
  orgType?: string;
  division?: string;
  repRole?: string;
  repEmail?: string;
  repPhone?: string;
  proofNumber?: string;
  proofDocName?: string | null;
  officialWebsite?: string;
  verificationStatus?: 'not_submitted' | 'pending' | 'verified' | 'rejected';
}

export interface UserProfile {
  id: string;
  name: string;
  username: string;
  initials: string;
  role: UserRole;
  plan: UserPlan;
  bio?: string;
  location?: string;
  avatarUrl?: string;
  verified: boolean;
  banned: boolean;
  profileCompleted?: boolean;
  profileBuilt?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerified?: boolean;
  playerFields?: PlayerProfileFields;
  clubFields?: ClubProfileFields;
}

let users: UserProfile[] = [];
let listeners: Array<() => void> = [];

export const profilesState = { loading: false, loaded: false, error: null as string | null };

function notify() { listeners.forEach((listener) => listener()); }

/** Load every profile from the backend into the in-memory cache. */
export async function loadProfiles(force = false): Promise<void> {
  if (profilesState.loading) return;
  if (profilesState.loaded && !force) return;
  profilesState.loading = true;
  profilesState.error = null;
  notify();
  try {
    const { fetchAllProfiles } = await import('@/services/profiles');
    const remote = await fetchAllProfiles();
    // Preserve locally-known private fields (email) for the signed-in user.
    users = remote.map((profile) => {
      const existing = users.find((u) => u.id === profile.id);
      return existing ? { ...profile, email: existing.email, emailVerified: existing.emailVerified } : profile;
    });
    profilesState.loaded = true;
  } catch (err) {
    console.error('[users] loadProfiles failed', err);
    profilesState.error = err instanceof Error ? err.message : 'Failed to load profiles';
  } finally {
    profilesState.loading = false;
    notify();
  }
}

function persist(id: string, patch: Partial<UserProfile>) {
  void import('@/services/profiles').then(({ saveProfile }) =>
    saveProfile(id, patch).then(({ avatarUrl }) => {
      // Replace the local data-URL with the uploaded storage URL.
      if (avatarUrl && avatarUrl !== getUserById(id)?.avatarUrl) {
        users = users.map((user) => (user.id === id ? { ...user, avatarUrl } : user));
        notify();
      }
    }),
  );
}

export function getUserById(id: string): UserProfile | undefined {
  return users.find((user) => user.id === id);
}

export function getUserByEmail(email: string): UserProfile | undefined {
  return users.find((user) => user.email?.toLowerCase() === email.toLowerCase());
}

export function getUserByUsername(username: string): UserProfile | undefined {
  return users.find((user) => user.username.toLowerCase() === username.toLowerCase());
}

export function getAllUsers(): UserProfile[] {
  return users;
}

export function toggleVerified(id: string) {
  const next = !getUserById(id)?.verified;
  users = users.map((user) => user.id === id ? { ...user, verified: next } : user);
  persist(id, { verified: next });
  notify();
}

export function toggleBanned(id: string) {
  const next = !getUserById(id)?.banned;
  users = users.map((user) => user.id === id ? { ...user, banned: next } : user);
  persist(id, { banned: next });
  notify();
}

export function changeUserPlan(id: string, plan: UserPlan) {
  users = users.map((user) => user.id === id ? { ...user, plan } : user);
  persist(id, { plan });
  notify();
}

export function updateUserProfile(id: string, data: Partial<UserProfile>) {
  users = users.map((user) => {
    if (user.id !== id) return user;
    const updated = { ...user, ...data };
    if (data.name) {
      const parts = data.name.trim().split(/\s+/);
      updated.initials = parts.map((part) => part[0]?.toUpperCase() ?? '').join('').slice(0, 2);
    }
    return updated;
  });
  const { email, emailVerified, initials, id: _id, ...persistable } = data;
  persist(id, persistable);
  notify();
}

export function markEmailVerified(id: string) {
  users = users.map((user) => user.id === id ? { ...user, emailVerified: true } : user);
  notify();
}

export function updateUserEmail(id: string, email: string) {
  users = users.map((user) =>
    user.id === id ? { ...user, email: email.toLowerCase(), emailVerified: false } : user,
  );
  notify();
}


export function addUser(profile: UserProfile) {
  users = users.some((user) => user.id === profile.id)
    ? users.map((user) => user.id === profile.id ? profile : user)
    : [...users, profile];
  notify();
}

export function removeUser(id: string) {
  users = users.filter((user) => user.id !== id);
  notify();
}

export function useUserUpdates() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((tick) => tick + 1);
    listeners.push(cb);
    return () => { listeners = listeners.filter((listener) => listener !== cb); };
  }, []);
}